import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors } from '@angular/forms';
import { buildProviders, Coerce, FormElementComponent } from '@kalidea/kaligraphi';
import { Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { UserState } from 'src/app/_user/store/user/user.state';
import { arrayNotEmpty } from 'src/app/utils/array.utils';
import { AppFile, formatFileSize } from 'src/app/utils/file.utils';

export type FileUploadError = { [errorKey: string]: any, errorMessage: string };

enum FileControlErrors {
    Filesize = 'filesize',
    Extension = 'fileextension',
    Empty = 'fileempty',
}

class FileUploadValidators {

    /**
     * Validator on file size
     * @param maxFileSize max file size in kB
     */
    static maxFileSize(maxFileSize: number) {
        return (control: AbstractControl): ValidationErrors | null => {
            const file = control.value;
            if (!file || !maxFileSize) {
                return null;
            }

            return (file.size / 1024) > maxFileSize
                ? {filesize: {maxFileSize}}
                : null;
        };
    }

    /**
     * Validator on extension
     * @param allowedExtensions array of allowed extensions for the current file upload
     */
    static fileExtensions(allowedExtensions: string[]) {
        return (control: AbstractControl): ValidationErrors | null => {
            const file = control.value;
            if (!file || !arrayNotEmpty(allowedExtensions)) {
                return null;
            }
            const splittedFileName = file.name.split('.');
            const actualExtension = (splittedFileName.length > 1 ? splittedFileName[splittedFileName.length - 1] : '');
            const possibleExtensions = [actualExtension.toLocaleLowerCase(), '.' + actualExtension.toLocaleLowerCase()];

            return !allowedExtensions.some(allowed => possibleExtensions.some(e => e === allowed))
                ? {fileextension: {allowedExtensions}}
                : null;
        };
    }

    static fileEmpty(control: AbstractControl): ValidationErrors | null {
        const file = control.value;
        return !file || file.size > 0 ? null : {fileempty: true};
    }

}

let uploadFileId = 0;

const MAX_DISPLAYED_EXTENSIONS = 5;

@Component({
    selector: 'app-simple-file-upload',
    templateUrl: './simple-file-upload.component.html',
    styleUrls: ['./simple-file-upload.component.sass'],
    providers: buildProviders(SimpleFileUploadComponent),
    standalone: false
})
export class SimpleFileUploadComponent extends FormElementComponent<AppFile> implements OnInit, OnDestroy {

    /**
     * Affiche un message en fonction de l'erreur du control.
     * Toutes les erreurs possibles sont dans ce tableau sauf pour la taille maximum qui est un cas particulier et peut-être affichée
     * soit en tant qu'erreur ou soit en tant que simple indication.
     */
    readonly messagesErrors: { message: string, controlError: FileControlErrors }[] = [
        {message: 'Le fichier est vide', controlError: FileControlErrors.Empty},
        {message: 'Les extensions autorisées sont : ', controlError: FileControlErrors.Extension},
        {message: 'La taille du fichier ne peut pas excéder ', controlError: FileControlErrors.Filesize},
    ];

    /**
     * The input accepts : file_extension|audio/*|video/*|image/*|media_type
     */
    @Input() extensions: string[] = [];

    /**
     * The id of the form
     */
    @Input() id = 'uploadFileId' + uploadFileId++;

    @Input() @Coerce('boolean') hideErrors = false;

    @Output() readonly fileReaded = new EventEmitter<AppFile>();

    @Output() readonly fileErrors = new EventEmitter<FileUploadError[]>();

    @ViewChild('fileUpload') fileUpload: ElementRef;

    @Input() maxSize: number | null = null;

    control = new FormControl();

    constructor(private readonly cdr: ChangeDetectorRef,
                private readonly store: Store) {
        super();
    }

    /**
     * show FileSizeMessage
     */
    get showFileSizeMessage(): boolean {
        return this.maxFileSize !== 0 && ((!!this.control.errors && this.hideErrors) || !this.hideErrors);
    }

    /**
     * The accepted extension
     */
    get allowedExtensions(): string {
        return this.extensions.join(', ');
    }

    /**
     * Shows the extensions cropped from the error message when it's extension context
     */
    get croppedExtensions(): string {
        return this.extensions.slice(MAX_DISPLAYED_EXTENSIONS).join(', ');
    }

    get formatedFileSize(): string {
        return formatFileSize(this.maxFileSize, 'Ko');
    }

    /**
     * The max file size defined in kB or 0 for no limit
     */
    private get maxFileSize(): number {
        return this.maxSize ?? this.store.selectSnapshot(UserState.maxFileSize);
    }

    private get hasMoreThanMaxDisplayableExtensions(): boolean {
        return this.extensions.length > MAX_DISPLAYED_EXTENSIONS;
    }

    click() {
        this.fileUpload.nativeElement.click();
    }

    validate(c: AbstractControl): Observable<any> {
        return of(this.control.errors);
    }

    hasFileError(errorKey: string): boolean {
        return this.control.hasError(errorKey);
    }

    /**
     * Uploading a file
     */
    readFile(file: File) {
        const reader = new FileReader();

        // Getting the file blob
        reader.readAsDataURL(file);

        // This callback is executed when the readAsDataURL is done
        reader.onload = () => {
            this.control.patchValue(file);

            if (this.control.valid) {
                const extension = this.getFileExtension(file);
                const appFile = new AppFile(file, reader.result as string, extension);

                this.notifyUpdate(appFile);
                this.fileReaded.emit(appFile);
                this.fileErrors.emit(null);
            } else {
                this.notifyUpdate(null);
                this.fileReaded.emit(null);

                const errors = [];
                Object.keys(this.control.errors).forEach(errorKey => {
                    errors.push({
                        [errorKey]: this.control.errors[errorKey],
                        errorMessage: this.getErrorMessage(errorKey)
                    });
                });

                this.fileErrors.emit(errors);
            }
        };
    }

    /**
     * Fired when a user selects a file
     */
    fileChange(event) {
        const fileList: FileList = event.target.files;

        if (fileList.length > 0) {

            // We the file that the user choose
            this.readFile(fileList.item(0));
        }

        // Empty the input file list
        event.target.value = null;
    }

    getErrorMessage(controlError: string): string {
        const message = this.messagesErrors.find(m => m.controlError === controlError).message || '';

        switch (controlError) {
            case FileControlErrors.Filesize:
                return message + this.formatedFileSize;
            case FileControlErrors.Extension:
                // si on a un grand nombre d'extensions autorisées, on affiche seulement les 5 premières
                const concatenateMessage = message + this.extensions.slice(0, MAX_DISPLAYED_EXTENSIONS).join(', ');
                return concatenateMessage + (this.hasMoreThanMaxDisplayableExtensions ? '...' : '');
            default:
                return message;
        }
    }

    canDisplayHiddenExtensions(controlError: string): boolean {
        return controlError === FileControlErrors.Extension && this.hasMoreThanMaxDisplayableExtensions;
    }

    private getFileExtension(file: File): string {
        const splittedFileName = file.name.split('.');
        return (splittedFileName.length > 1 ? splittedFileName[splittedFileName.length - 1] : '');
    }

    ngOnInit(): void {
        // Add validators on the file control
        this.control.setValidators([
            ...(this.maxFileSize !== 0 ? [FileUploadValidators.maxFileSize(this.maxFileSize)] : []),
            FileUploadValidators.fileExtensions(this.extensions),
            FileUploadValidators.fileEmpty,
        ]);
        this.cdr.markForCheck();
    }
}
